import { useEffect, useRef } from 'react';
import {
  getNamespacedSocket,
  disconnectNamespacedSocket,
} from '../api/socket';

const NAMESPACE = '/master-data';
const ORG_SLUG = process.env.REACT_APP_ORG_SLUG ?? 'sahari-cargo';
/** Collapse a burst of changes (e.g. a bulk upload) into one refetch. */
const DEBOUNCE_MS = 300;

/**
 * Live master-data updates for the public site.
 *
 * Connects (tokenless) to the backend's `/master-data` socket namespace and
 * subscribes to this org's room by slug. Whenever the admin adds / edits /
 * removes / deactivates a master record, the server pushes a
 * `master-data:changed` refetch nudge; we call `onChange(resource)` so the
 * page can re-pull just the affected dropdown — no page refresh.
 *
 * Resources of interest here: `geography` (countries + cities),
 * `commodity-types`, `payment-types`.
 */
export function useMasterDataLive(onChange: (resource: string) => void) {
  // Keep the latest callback without re-running the connect effect.
  const cbRef = useRef(onChange);
  cbRef.current = onChange;

  useEffect(() => {
    const socket = getNamespacedSocket(NAMESPACE);
    if (!socket.connected) socket.connect();

    const subscribe = () => socket.emit('subscribe', { orgSlug: ORG_SLUG });
    const onConnect = () => subscribe();

    const timers: Record<string, ReturnType<typeof setTimeout>> = {};
    const onChanged = (payload: { resource?: string }) => {
      const res = payload?.resource;
      if (!res) return;
      clearTimeout(timers[res]);
      timers[res] = setTimeout(() => cbRef.current(res), DEBOUNCE_MS);
    };

    socket.on('connect', onConnect);
    socket.on('master-data:changed', onChanged);
    if (socket.connected) subscribe();

    return () => {
      socket.off('connect', onConnect);
      socket.off('master-data:changed', onChanged);
      Object.values(timers).forEach((t) => clearTimeout(t));
      disconnectNamespacedSocket(NAMESPACE);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
